import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import { isEqual, omit } from "lodash";
import PropTypes from "prop-types";
import { useFormik } from "formik";

import { generateFormData, getImageUrl, isAuthorizedUser } from "utils";
import InputFieldText from "Components/Common/InputFieldText";

import { getOptionValue } from "common/enums";
import {
  otpVerificationType,
  utilityTunnelConsumerRoles,
} from "common/constant";
import Backdrop from "Components/Common/Backdrop";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import VerifyEmailModal from "Components/Common/VerifyEmailModal";
import ChangePasswordModal from "./ChangePasswordModal";
import {
  AdminProfileValidationSchema,
  UserProfileValidationSchema,
  initialValuesForAdminProfileDetails,
  initialValuesForProfileDetails,
} from "./UserProfileValidationSchema";

import getFileExtension from "utils/getFileExtension";

import {
  useLazyGetUserQuery,
  useLazyGetUtilityUserQuery,
  usePutAdminProfileMutation,
  usePutConsumerProfileMutation,
  usePutUploadDigitalSignatureMutation,
} from "apis/User";
import { useRequestOTPMutation } from "apis/OTP";

import {
  UploadProfileImage,
  ProfileDetailsBox,
  AdminProfileImageBox,
  DigitalSignatureContainer,
  UploadSignature,
} from "./style";
import { useGetRolesQuery } from "apis/Role";
import { loginSuccess } from "slices/auth/login/reducer";
import SignaturePreviewModal from "Components/Common/SignaturePreviewModal";

const imageExt = ["jpg", "png", "jpeg"];

const UtilityTunnelProfileDetails = ({ t }) => {
  const {
    user: { id: userId },
    roles,
  } = useSelector((state) => state.Login);

  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessageForSignature, setErrorMessageForSignature] = useState("");
  const [documentToPreview, setDocumentToPreview] = useState();
  const [uploadedFile, setUploadedFile] = useState();
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const dispatch = useDispatch();

  const [
    getUserData,
    { data: { data: user = {} } = {}, isLoading: isUserDataLoading },
  ] = useLazyGetUserQuery();

  const [getUtilityUserData] = useLazyGetUtilityUserQuery();

  const {
    data: { data: rolesOptions } = {},
    isLoading: isRolesOptionsLoading,
  } = useGetRolesQuery({}, false, true);
  const [editConsumerProfileDetails, { isLoading: isConsumerProfileUpdating }] =
    usePutConsumerProfileMutation();
  const [uploadSignature, { isLoading }] =
    usePutUploadDigitalSignatureMutation();

  const [requestOTP, { isLoading: isRequestOTPLoading }] =
    useRequestOTPMutation();

  const [editAdminProfileDetails, { isLoading: isAdminProfileUpdating }] =
    usePutAdminProfileMutation();

  const isConsumer = useMemo(
    () => isAuthorizedUser(roles, utilityTunnelConsumerRoles),
    [roles]
  );

  const initialValues = useMemo(
    () =>
      isConsumer
        ? initialValuesForProfileDetails(user)
        : initialValuesForAdminProfileDetails(user, rolesOptions),
    [user, rolesOptions, isConsumer]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: isConsumer
      ? UserProfileValidationSchema
      : AdminProfileValidationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        const updatedValues = {
          ...values,
          ...(!isConsumer && {
            departmentRoles: values?.departmentRoles.map(
              ({ roles, departmentId }) => ({
                roles: roles.map(({ value }) => value),
                departmentIds: departmentId ? [departmentId] : [],
              })
            ),
          }),
        };

        generateFormData(formData, omit(updatedValues, ["signatureImage"]));
        isConsumer
          ? await editConsumerProfileDetails(formData, false, true)
          : await editAdminProfileDetails(formData, {}, false, true);

        const { data: userData } = await getUtilityUserData(
          userId,
          {},
          false,
          true
        );
        dispatch(loginSuccess({ roles, user: userData }));
      } catch (err) {
        console.error("error", err);
      }
    },
  });

  const { values, resetForm, setFieldValue, setFieldTouched, handleSubmit } =
    formik;

  const handleUploadImage = (e) => {
    setFieldTouched("file", true, true);
    if (e.target.files?.length) {
      const isError = checkValidationForProfileImage(e.target.files[0]);
      if (!isError) {
        setFieldValue("profileImageURL", "");
        setFieldValue("file", e.target.files[0]);
        e.target.value = null;
      }
    }
  };

  const checkValidationForProfileImage = (file) => {
    const fileExtension = getFileExtension(file?.name).toLowerCase();
    if (imageExt.includes(fileExtension)) {
      if (file?.size <= 614400) {
        setErrorMessage("");
        return false;
      }
      setErrorMessage("Maximum size of image is 600kb");
      return true;
    }
    setErrorMessage("Only image is allowed");
    return true;
  };

  const checkValidationForSignatureImage = (file) => {
    const fileExtension = getFileExtension(file?.name).toLowerCase();
    if (imageExt.includes(fileExtension)) {
      if (file?.size <= 614400) {
        setErrorMessageForSignature("");
        return false;
      }
      setErrorMessageForSignature("Maximum size of image is 600kb");
      return true;
    }
    setErrorMessageForSignature("Only image is allowed");
    return true;
  };

  const handleDeleteProfileImage = () => {
    setFieldValue("file", "");
    setFieldValue("profileImageURL", "");
    setFieldTouched("file", false, false);
    setErrorMessage("");
  };

  const generateOTP = async (email) => {
    try {
      await requestOTP(
        {
          email: encodeURIComponent(email),
          type: otpVerificationType["Signature"],
        },
        "",
        false,
        true
      );
      setIsOTPSent(true);
    } catch (error) {}
  };

  const isDetailsChanged = useMemo(() => {
    return isEqual(
      isConsumer
        ? initialValuesForProfileDetails(user)
        : initialValuesForAdminProfileDetails(user, rolesOptions),
      values
    );
  }, [values, user, rolesOptions]);

  const handleSignatureUploader = async (e) => {
    if (e.target.files?.length) {
      const isError = checkValidationForSignatureImage(e.target.files[0]);
      if (!isError) {
        const img = document.createElement("img");
        img.setAttribute("src", URL.createObjectURL(e.target.files[0]));
        img.onload = () => {
          setDocumentToPreview({
            url: URL.createObjectURL(e.target.files[0]),
            documentTypeName: "Digital Signature",
            documentType: getFileExtension(e.target.files[0]?.name),
            documentName: "Digital Signature",
          });
          setUploadedFile(e.target.files[0]);
          e.target.value = null;
        };
      }
    }
  };

  const handleSubmitForDigitalSignature = async () => {
    try {
      const formData = new FormData();
      formData.append("formFile", uploadedFile);
      await uploadSignature(formData, false, true);
      const { data: userData } = await getUtilityUserData(
        userId,
        {},
        false,
        true
      );
      dispatch(loginSuccess({ roles, user: userData }));
      setDocumentToPreview(null);
    } catch (error) {
      console.error(error);
    }
  };

  const handleClose = () => {
    setDocumentToPreview(null);
    setUploadedFile(null);
  };

  useEffect(() => {
    try {
      getUserData(userId, {}, false, true);
    } catch (error) {}
  }, [userId]);

  return (
    <ProfileDetailsBox>
      {isChangePasswordModalOpen && (
        <ChangePasswordModal
          handleClose={() => setIsChangePasswordModalOpen(false)}
        />
      )}
      <Backdrop open={isUserDataLoading} />
      {(isRequestOTPLoading || isOTPSent) && (
        <VerifyEmailModal
          isOpen={isOTPSent}
          handleClose={() => setIsOTPSent(false)}
          handleOTPverificationSuccess={handleSubmitForDigitalSignature}
          email={values.email}
          isLoading={isRequestOTPLoading}
          requestOTPType={otpVerificationType["Signature"]}
          isUtilityTunnel={true}
        />
      )}

      {!!documentToPreview && (
        <SignaturePreviewModal
          isOpen
          handleClose={handleClose}
          isDownloadable={false}
          actionButton={
            <Button
              onClick={() => generateOTP(values.email)}
              color="primary"
              className="mb-1 me-1"
              variant="contained"
              disabled={isLoading}
            >
              {t("Upload")} {isLoading && <CircularProgress size={20} />}
            </Button>
          }
          {...documentToPreview}
        />
      )}
      {isConsumer ? (
        <form onSubmit={handleSubmit}>
          <Grid item xs={12}>
            <Grid item container className="mb-2">
              <Grid item xs={12} sm={6}>
                <UploadProfileImage>
                  <Avatar
                    src={
                      values.file
                        ? URL.createObjectURL(values.file)
                        : getImageUrl(values.profileImageURL)
                    }
                    className="avatar"
                  />
                  <Typography variant="h6" className="user-name">
                    {`${user?.firstName ?? ""} ${user?.lastName ?? ""}`}
                  </Typography>
                  <Box className="profile-action-button">
                    <Button size="small" variant="outlined" component="label">
                      {t(
                        values.file || values.profileImageURL ? "Edit" : "Add"
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleUploadImage}
                        hidden
                      />
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={handleDeleteProfileImage}
                      disabled={
                        values.profileImageURL?.length === 0 &&
                        values.file?.length === 0
                      }
                    >
                      {t("Delete")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => setIsChangePasswordModalOpen(true)}
                    >
                      {t("Change Password")}
                    </Button>
                  </Box>
                  {errorMessage ? (
                    <Typography className="error-message">
                      {errorMessage}
                    </Typography>
                  ) : null}
                </UploadProfileImage>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DigitalSignatureContainer>
                  {values.signatureImage ? (
                    <img
                      src={getImageUrl(values.signatureImage)}
                      style={{ width: 276, height: 119, objectFit: "contain" }}
                    />
                  ) : (
                    <UploadSignature>
                      {t("Upload")} <br /> {t("Signature")}
                    </UploadSignature>
                  )}
                  {!!errorMessageForSignature && (
                    <Typography color="error" className="text-center">
                      {errorMessageForSignature}
                    </Typography>
                  )}
                </DigitalSignatureContainer>

                <Typography variant="h6" className="user-name text-center my-2">
                  {t("Signature")}
                </Typography>
                <Box className="d-flex gap-3 justify-content-center mb-2">
                  <Button size="small" variant="outlined" component="label">
                    {t(values.signatureImage ? "Edit" : "Add")}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSignatureUploader}
                      hidden
                    />
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <Typography variant="h6" className="information-header">
              {t("Personal Information")}
            </Typography>
            <Divider />
            <Grid container columnSpacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <InputFieldText
                  label={t("Email") + "*"}
                  name="email"
                  formik={formik}
                  placeholder="Enter Email"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InputFieldText
                  label={t("Mobile Number") + "*"}
                  name="mobileNumber"
                  formik={formik}
                  placeholder="Enter Mobile Number"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InputFieldText
                  label={t("First Name") + "*"}
                  name="firstName"
                  formik={formik}
                  placeholder="Enter First Name"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <InputFieldText
                  label={t("Last Name")}
                  name="lastName"
                  formik={formik}
                  placeholder="Enter Last Name"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {!isDetailsChanged && !errorMessage && (
              <Box className="d-flex justify-content-end gap-3">
                <Button
                  variant="contained"
                  size="small"
                  type="reset"
                  onClick={() => resetForm()}
                >
                  {t("Reset")}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  type="submit"
                  disabled={isConsumerProfileUpdating}
                >
                  {t("Save")}
                  {isConsumerProfileUpdating ? (
                    <CircularProgress sx={{ marginLeft: "5px" }} size={20} />
                  ) : null}
                </Button>
              </Box>
            )}
          </Grid>
        </form>
      ) : (
        <form onSubmit={handleSubmit} className="mt-5">
          <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
            <Grid item xs={12} xl={6}>
              <Grid item container className="mb-2">
                <Grid item xs={12} sm={6}>
                  <AdminProfileImageBox>
                    {values?.profileImageURL || values?.file ? (
                      <img
                        src={
                          values.file
                            ? URL.createObjectURL(values.file)
                            : getImageUrl(values.profileImageURL)
                        }
                        className="image-box"
                      />
                    ) : (
                      <AccountBoxIcon className="dashboard-icon" />
                    )}
                    {errorMessage ? (
                      <Typography color="error" className="text-center">
                        {errorMessage}
                      </Typography>
                    ) : null}
                  </AdminProfileImageBox>
                  <Typography
                    variant="h6"
                    className="user-name text-center my-2"
                  >
                    {`${user?.firstName ?? ""} ${user?.lastName ?? ""}`}
                  </Typography>

                  <Box className="d-flex gap-3 justify-content-center mb-2">
                    <Button size="small" variant="outlined" component="label">
                      {values.file || values.profileImageURL
                        ? t("Edit")
                        : t("Add")}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleUploadImage}
                        hidden
                      />
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={handleDeleteProfileImage}
                      disabled={
                        values.profileImageURL?.length === 0 &&
                        values.file?.length === 0
                      }
                    >
                      {t("Delete")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => setIsChangePasswordModalOpen(true)}
                    >
                      {t("Change Password")}
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <DigitalSignatureContainer>
                    {values.signatureImage ? (
                      <img src={getImageUrl(values.signatureImage)} />
                    ) : (
                      <UploadSignature>
                        {t("Upload")} <br /> {t("Signature")}
                      </UploadSignature>
                    )}
                    {!!errorMessageForSignature && (
                      <Typography color="error" className="text-center">
                        {errorMessageForSignature}
                      </Typography>
                    )}
                  </DigitalSignatureContainer>

                  <Typography
                    variant="h6"
                    className="user-name text-center my-2"
                  >
                    {t("Signature")}
                  </Typography>
                  <Box className="d-flex gap-3 justify-content-center mb-2">
                    <Button size="small" variant="outlined" component="label">
                      {t(values.signatureImage ? "Edit" : "Add")}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleSignatureUploader}
                        hidden
                      />
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} xl={6}>
              <Grid item>
                <InputFieldText
                  label={t("User Name") + "*"}
                  name="userName"
                  formik={formik}
                  placeholder="Enter User Name"
                />
              </Grid>
              <Grid item>
                <InputFieldText
                  label={t("First Name") + "*"}
                  name="firstName"
                  formik={formik}
                  placeholder="Enter First Name"
                />
              </Grid>
              <Grid item>
                <InputFieldText
                  label={t("Last Name")}
                  name="lastName"
                  formik={formik}
                  placeholder="Enter Last Name"
                />
              </Grid>
              <Grid item>
                <InputFieldText
                  label={t("Email") + "*"}
                  name="email"
                  formik={formik}
                  placeholder="Enter Email"
                />
              </Grid>

              {!isDetailsChanged && !errorMessage && (
                <Box className="d-flex justify-content-end gap-3">
                  <Button
                    variant="contained"
                    size="small"
                    type="reset"
                    onClick={() => resetForm()}
                  >
                    {t("Reset")}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    type="submit"
                    disabled={isAdminProfileUpdating}
                  >
                    {t("Save")}
                    {isAdminProfileUpdating ? (
                      <CircularProgress sx={{ marginLeft: "5px" }} size={20} />
                    ) : null}
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </form>
      )}
    </ProfileDetailsBox>
  );
};

UtilityTunnelProfileDetails.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(UtilityTunnelProfileDetails);
